import React, {useState, useEffect} from 'react'
import _ from 'lodash'
import {ValueInputWithRange, IconText, Icon, Text} from '../../../sharedComponents/components'
import {colors} from '../../../sharedComponents/colors'

import InputWithTags from './InputWithTags'
import {useTranslation} from 'react-i18next'
import PerformTaskForm from './PerformTaskForm'
import PerformTaskTitle from './PerformTaskTitle'
import {handleFloatInput, handleWeightValue} from '../../../config/utils'
import {useAppState} from '../../../state'
import {getWasteUnit, WasteTaskType} from '../../../Components/Views/Tasks/CreateWasteTask'
import {EventStatusCode, PerformableTask} from '../../../state/performTask/state'
import {FormContainer} from './PerformCoolerCoolingTask'
import ReactDatePicker from 'react-datepicker'
import styled from 'styled-components'
import {startOfMonth, subMonths, endOfDay, isSameDay} from 'date-fns'

const CustomDateInputWrapper = styled.div`
  position: relative;
  width: 100%;

  /* react-datepicker renders a clear button with class 'react-datepicker__close-icon' inside the input wrapper
     We'll target the SVG path inside it to change the color depending on wrapper class. */
  &.default-date {
    /* when our custom input has this class, the close icon is rendered as a sibling by react-datepicker */
    & + .react-datepicker__close-icon,
    & + .react-datepicker__close-icon::after,
    & + .react-datepicker__close-icon svg {
      cursor: pointer;
      background-color: #e5e5ea !important;
      color: #000;
      border-radius: 50%;
      height: 25px !important;
      width: 25px !important;
      padding: 0px;
      font-size: 16px;
      line-height: 1;
      text-align: center;
      display: table-cell;
      vertical-align: middle;
      content: '×';
      margin-top: 6px;
      margin-right: 4px;
    }
    & + .react-datepicker__close-icon svg path {
      stroke: #e5e5ea !important;
      fill: #e5e5ea !important;
    }
  }

  &.changed-date {
    & + .react-datepicker__close-icon,
    & + .react-datepicker__close-icon::after,
    & + .react-datepicker__close-icon svg {
      cursor: pointer;
      background-color: #40a8f5 !important;
      color: #000;
      border-radius: 50%;
      height: 25px !important;
      width: 25px !important;
      padding: 0px;
      font-size: 16px;
      line-height: 1;
      text-align: center;
      display: table-cell;
      vertical-align: middle;
      content: '×';
      margin-top: 6px;
      margin-right: 4px;
    }
    & + .react-datepicker__close-icon svg path {
      stroke: #40a8f5 !important;
      fill: #40a8f5 !important;
    }
  }
`

const CustomInputField = styled.input`
  width: 100%;
  padding: 0.5rem 2.5rem 0.5rem 0.5rem;
  border: 1px solid ${colors.system.grey_5};
  border-radius: 4px;
  height: 38px;
  box-sizing: border-box;
`

const CalendarIcon = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`

const CustomDateInput = React.forwardRef(({value, onClick, className}: any, ref: any) => (
  <CustomDateInputWrapper className={className}>
    <CustomInputField readOnly ref={ref} value={value} onClick={onClick} />
  </CustomDateInputWrapper>
))
const WASTE_AMOUNT_MISTAKE_THRESHOLD_IN_KG = 100

interface PerformWasteTaskProps {
  task: PerformableTask
  handleNumberBlur: (event: any) => void
  handleRemove: () => void
  setValue: any
  onAssetSaved: () => void
  onAssetRemoved: (assetId: string) => void
  payload: any
  handleSubmitAndAddNew?: () => void
  handleSubmit?: (dontClose?: boolean) => Promise<void> | void
  disabled?: boolean
  hasCurrentEvent?: boolean
  currentEventId?: string
}

const PerformWasteTask = ({
  task,
  setValue,
  onAssetSaved,
  onAssetRemoved,
  payload,
  handleNumberBlur,
  handleRemove,
  handleSubmitAndAddNew,
  handleSubmit,
  disabled,
  hasCurrentEvent,
  currentEventId
}: PerformWasteTaskProps) => {
  const {state} = useAppState()
  const {t} = useTranslation('tasks')
  // ensure payload.recordingDay defaults to today when not provided
  const today = endOfDay(new Date())
  useEffect(() => {
    if (!payload.recordingDay) {
      try {
        setValue('recordingDay', new Date(today).toISOString())
      } catch (e) {
        // ignore if setValue cannot be called here
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [isRequired, setIsRequired] = useState(true)
  const handleRemoveTask = () => {
    setIsRequired(false)
    if (handleRemove) {
      handleRemove()
    }
  }

  const isWasteAmountTooMuch =
    (task.wasteTaskType === WasteTaskType.WasteAmount || task.wasteTaskType === WasteTaskType.ProductionAmount) &&
    _.toNumber(payload.value) >= WASTE_AMOUNT_MISTAKE_THRESHOLD_IN_KG

  // determine whether the recordingDay currently equals the default (today)
  const isRecordingDayDefault = (() => {
    if (!payload.recordingDay) return true
    try {
      return isSameDay(new Date(payload.recordingDay), today)
    } catch (e) {
      return false
    }
  })()
  return (
    <FormContainer>
      <PerformTaskTitle task={task} />
      <ValueInputWithRange
        name="value-input"
        value={payload.value}
        defaultValue={task.value}
        label={t('tasks:labels.measuredValue', 'Measured value')}
        onChange={e => setValue('value', handleFloatInput(e.target.value))}
        onBlur={e => handleNumberBlur(e.target.value)}
        unit={getWasteUnit(state.site!, task.wasteTaskType as any).toLowerCase()}
        maxValue={handleWeightValue(state.site!, task.maxValue)}
        message={{
          success: t('tasks:inputWithRange.withinTarget', 'The value is within target limits'),
          error: t('tasks:inputWithRange.not.withInTarget', 'The value is not within target')
        }}
        reminder={
          isWasteAmountTooMuch && (
            <IconText
              icon={<Icon type="exclamation-circle" fill={colors.system.red} width="1rem" height="1rem" />}
              text={
                <Text size="S" color={colors.system.red}>
                  {t(
                    'tasks:labels.wasteTooMuch.reminder',
                    `Do you mean {{currentValue}} g? That's {{suggestValue}} kg`,
                    {currentValue: payload.value, suggestValue: _.toNumber(payload.value) / 1000}
                  )}
                </Text>
              }
            />
          )
        }
        required={isRequired}
      />
      {/* Recording day (single date) - shown under Measured value */}
      <div style={{marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
        <label style={{fontSize: '0.89rem'}}>{t('tasks:labels.recordingDay', 'Recording day')}</label>
        <ReactDatePicker
          selected={payload.recordingDay ? new Date(payload.recordingDay) : today}
          onChange={(value: Date | null) => {
            setValue('recordingDay', value ? value.toISOString() : undefined)
          }}
          minDate={startOfMonth(subMonths(today, 1))}
          maxDate={today}
          dateFormat="dd.MM.y"
          isClearable
          placeholderText={t('common:labels.calendar.start', 'Start Date')}
          locale={state.me?.user.language || undefined}
          customInput={<CustomDateInput className={isRecordingDayDefault ? 'default-date' : 'changed-date'} />}
        />
      </div>
      {task.wasteTaskType === WasteTaskType.WasteAmount && (
        <>
          <InputWithTags
            savedSuggestions={payload.ingredients}
            defaultSuggestions={task.ingredientSuggestions || []}
            label={t('tasks:labels.measurementTarget', 'Measurement target')}
            placeholder={t('tasks:placeholder.measurementTargetWaste', 'Measurement target')}
            onChange={setValue}
            keyName="ingredients"
            maxCharacters={100}
          />
          <InputWithTags
            label={t('tasks:labels.wasteReason', 'Enter the reason for waste')}
            placeholder={t('tasks:placeholder.wasteReason', 'Enter the reason for waste')}
            defaultSuggestions={task.defaultSuggestions || []}
            onChange={setValue}
          />
        </>
      )}
      <PerformTaskForm
        onInputChange={setValue}
        onAssetSaved={onAssetSaved}
        onAssetRemoved={onAssetRemoved}
        payload={payload}
        handleSubmitAndAddNew={handleSubmitAndAddNew}
        handleSubmit={handleSubmit}
        handleRemove={handleRemoveTask}
        setValue={setValue}
        disabled={disabled}
        confirm={t(
          'tasks:labels.confirmRemoveTask',
          'Are you sure you want to delete this task? This will also remove any associated resolved alarms.'
        )}
        hasCurrentEvent={!!hasCurrentEvent}
        currentEventId={currentEventId}
      />
    </FormContainer>
  )
}

export default PerformWasteTask
